import { AxiosError } from "axios";
import { Flex, Link, LoadingIcon } from "basis";
import { InfoBlock } from "components/InfoBlock/InfoBlock";
import { differenceInDays, format } from "date-fns";
import { getTransactions } from "library/api";
import { ApiGetResponse, transactionPath } from "library/api/transaction";
import { useProducts } from "library/hooks/useProducts";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { ErrorPanel } from "../../ErrorPanel/ErrorPanel";
import { Pagination } from "./Pagination";
import { TransactionRow } from "./TransactionRow";
import styles from "./transactions.module.scss";

interface Props {
  accountId: string;
  productId: string;
}

const getTransactionList = (responseData) => {
  if (!responseData) return [];

  const { transactions = [] } = responseData.data;

  const groupedTransactions = transactions.reduce((result, trans) => {
    const newKey =
      trans.status === "PENDING" ? trans.status : trans.transaction_date;
    result[newKey] = [...(result[newKey] || []), trans];
    return result;
  }, {});

  return Object.keys(groupedTransactions).map((key) => {
    const trans = groupedTransactions[key];
    let title = key;

    if (key !== "PENDING") {
      const diff = differenceInDays(
        new Date(trans[0].transaction_date),
        new Date(),
      );

      if (diff === 0) {
        title = "TODAY";
      } else if (diff === -1) {
        title = "YESTERDAY";
      } else {
        title = format(new Date(trans[0].transaction_date), "dd MMM yyyy");
      }
    }

    return { title, transactions: trans };
  });
};

export const Transactions = ({ accountId, productId }: Props) => {
  const [href, setHref] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const { data, isError, isLoading } = useQuery<ApiGetResponse, AxiosError>(
    [href],
    () => getTransactions({ href }),
  );

  useEffect(() => {
    setHref(transactionPath(accountId));
  }, [accountId]);

  const productsInfo = useProducts();
  const product = productsInfo.data?.find((p) => p.id === productId);
  const transactionList = getTransactionList(data);
  const prevPageLink = data?.data.links.find(
    (el) => el.rel === "previous_page",
  );
  const nextPageLink = data?.data.links.find((el) => el.rel === "next_page");

  const previousPageClicked = () => {
    if (page === 2) {
      setHref(transactionPath(accountId));
    } else if (prevPageLink) {
      setHref(prevPageLink.href);
    }
    setPage(page - 1);
  };
  const nextPageClicked = () => {
    setPage(page + 1);
    if (nextPageLink) setHref(nextPageLink.href);
  };

  if (isLoading) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }
  if (isError) {
    return (
      <ErrorPanel icon="error-cards" data-trackid="api-error-info">
        We were unable to retrieve your transaction history as there has been an
        issue on our end. Please check back again soon or{" "}
        <Link href="/contact" newTab={false}>
          contact support
        </Link>{" "}
        for help.
      </ErrorPanel>
    );
  }

  if (data?.data.transactions?.length === 0) {
    return (
      <InfoBlock iconName="no-transactions" trackId="no-transactions">
        {`You haven't made any transactions${
          product ? ` on your ${product.name}` : ""
        } yet.`}
      </InfoBlock>
    );
  }

  return (
    <div
      id="transaction-list"
      className={styles.transaction}
      role="table"
      aria-label="Transactions"
    >
      <div role="rowgroup">
        <div className={styles.column} role="row">
          <span className={styles.columnDate} role="columnheader">
            DATE
          </span>
          <span className={styles.columnDesc} role="columnheader">
            DESCRIPTION
          </span>
          <span className={styles.columnAmount} role="columnheader">
            AMOUNT
          </span>
        </div>
      </div>
      {transactionList.map((list, i) => {
        const { transactions } = list;
        let tgn = styles.group;

        if (list.title === "PENDING") {
          tgn += `  ${styles.highlight}`;
        }

        return (
          <div className={tgn} key={i} role="row">
            <div role="cell" className={styles.date} data-trackid="row-title">
              {list.title}
            </div>
            <div className={styles.groupTransactionList} role="rowgroup">
              {transactions.map((transaction, j) => (
                <TransactionRow transaction={transaction} index={j} key={j} />
              ))}
            </div>
          </div>
        );
      })}

      <Pagination
        page={page}
        nextPageLink={nextPageLink}
        previousPageClicked={previousPageClicked}
        nextPageClicked={nextPageClicked}
      />
    </div>
  );
};
